// Linked sizes (products.size_of_product_id): one item sold several ways — by the case,
// the half case, the single, the pound. Each size is its own product (own name, price,
// unit, QuickBooks item, order lines); the link only groups them. Shared by the size
// routes (server) and the menu and product form (client). Pure functions only.

export const PRODUCT_NAME_SEPARATOR = " - ";

// What a size's generated name ends with, where the bare unit reads badly after a
// title that already carries a weight ("Bananas - 40lb - lb").
const UNIT_NAME_SUFFIX: Record<string, string> = {
  lb: "per lb",
  kg: "per kg",
};

/**
 * The name a new size is created under: the lead's name plus the size's unit —
 * "Oranges - 56" + "half case" → "Oranges - 56 - half case". Product names are unique
 * per org and become the QuickBooks item name, so every size needs its own.
 */
export function sizeProductName(leadName: string, unit: string): string {
  return `${leadName.trim()}${PRODUCT_NAME_SEPARATOR}${sizeUnitSuffix(unit)}`;
}

/** What a unit reads as on the end of a size's name: "half case", "per lb". */
export function sizeUnitSuffix(unit: string): string {
  return UNIT_NAME_SUFFIX[unit] ?? unit;
}

// The units the product form offers first for a new size.
export const SIZE_UNIT_SUGGESTIONS = ["half case", "each", "lb"] as const;

/**
 * The share of a case a new size starts with, where the unit alone says it. A single
 * or a pound depends on the pack, so those start with none.
 */
export function defaultCaseFraction(unit: string): string | null {
  return unit === "half case" ? "0.5" : null;
}

/**
 * A size's name after its lead was renamed: the same ending on the new lead name. Null
 * when the size's name does not start with the lead's old name — a name the admin
 * wrote by hand ("Lemons single") is left alone.
 */
export function sizeNameAfterLeadRename(sizeName: string, oldLeadName: string, newLeadName: string): string | null {
  const from = oldLeadName.trim();
  const to = newLeadName.trim();
  if (!from || from === to || !sizeName.startsWith(from + PRODUCT_NAME_SEPARATOR)) return null;
  return to + sizeName.slice(from.length);
}

/**
 * case_fraction from a request body: a positive number, stored as text for the numeric
 * column. `undefined` = not sent, null or "" = clear it.
 */
export function readCaseFraction(raw: unknown): { caseFraction?: string | null; error?: string } {
  if (raw === undefined) return {};
  if (raw === null || raw === "") return { caseFraction: null };
  const value = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw.trim()) : NaN;
  if (!Number.isFinite(value) || value <= 0 || value > 1000) {
    return { error: "The share of a case is a number above zero, like 0.5." };
  }
  return { caseFraction: String(value) };
}

export type SizeGroup<P> = {
  /** The lead's id, or the product's own id when it is in no group. */
  key: number;
  /** The product whose name and photo head the card: the lead when it is in the list. */
  head: P;
  /** Every size in the list, in the order the admin set (unitOrder), else lead first, then largest share of a case first. One entry = an ordinary card. */
  members: P[];
};

/**
 * A product list as the menu draws it: linked sizes collapsed into one group, placed
 * where the group's first product sat. Only products in the list are grouped — a size
 * whose lead is hidden from this customer still appears, heading its own group — so the
 * grouping never shows anything the list did not already contain.
 */
export function groupProductSizes<
  P extends { id: number; sizeOfProductId?: number | null; caseFraction?: string | null; unitOrder?: number | null },
>(list: P[]): SizeGroup<P>[] {
  const groups = new Map<number, SizeGroup<P>>();
  for (const product of list) {
    const key = product.sizeOfProductId ?? product.id;
    const group = groups.get(key);
    if (!group) {
      groups.set(key, { key, head: product, members: [product] });
    } else {
      if (product.id === key) group.head = product;
      group.members.push(product);
    }
  }
  // The order the admin dragged the units into comes first (unitOrder). A unit with none
  // — every unit of an item nobody ordered, or one added since — follows: lead, then half
  // case before each (the largest share of a case first). Sizes with no share stored keep
  // their list order, after the ones that have it.
  const rank = (product: P, key: number) =>
    product.id === key ? Infinity : product.caseFraction ? Number(product.caseFraction) : 0;
  const placed = (product: P) => product.unitOrder ?? Infinity;
  return Array.from(groups.values(), (group) =>
    group.members.length > 1
      ? {
          ...group,
          members: group.members
            .slice()
            .sort((a, b) =>
              placed(a) === placed(b) ? rank(b, group.key) - rank(a, group.key) : placed(a) < placed(b) ? -1 : 1,
            ),
        }
      : group,
  );
}

export type SizeCardLabels = {
  /** The card's heading. */
  title: string;
  /** One per member, in order: what its row reads beside its unit tag. "" = the tag alone says it. */
  rows: string[];
};

const sameText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: "accent" }) === 0;

/**
 * How a size group reads on its menu card. Every size keeps its whole name — it is what
 * invoices and pick sheets print — and the card says the part they share once: the
 * heading is what the names have in common, cut at a " - ", and each row is the rest of
 * its own name.
 *
 *   "Blueberries - 12×6oz" [case] + "Blueberries - 6oz" [each]
 *     → "Blueberries": "12×6oz" · "6oz"
 *   "Oranges - 56" [case] + "Oranges - 56 - half case" [half case]
 *     → "Oranges - 56": "" · ""   (the tag already says "half case")
 *
 * Names that share no opening part ("Lemons - 115" + "Lemons single") leave the card as
 * it was before: headed by `headName`, every row "".
 */
export function sizeCardLabels(
  members: { name: string; unit?: string | null }[],
  headName: string,
): SizeCardLabels {
  const parts = members.map((member) => member.name.split(PRODUCT_NAME_SEPARATOR).map((part) => part.trim()));
  let shared = 0;
  while (parts.every((own) => shared < own.length && sameText(own[shared], parts[0][shared]))) shared++;
  if (members.length < 2 || shared === 0) return { title: headName, rows: members.map(() => "") };

  const rows = parts.map((own, i) => {
    const rest = own.slice(shared);
    // A name that ends in its own unit ("… - half case", "… - per lb"): the tag says it.
    const unit = members[i].unit;
    const last = rest[rest.length - 1];
    if (unit && last !== undefined && (sameText(last, unit) || sameText(last, UNIT_NAME_SUFFIX[unit] ?? unit))) rest.pop();
    return rest.join(PRODUCT_NAME_SEPARATOR);
  });
  return { title: parts[0].slice(0, shared).join(PRODUCT_NAME_SEPARATOR), rows };
}

const headingKey = (title: string) => title.trim().toLowerCase();

/**
 * How every card in one drawn list reads: one entry per group, in the groups' order.
 * Each group reads as `sizeCardLabels` has it, unless that would leave two cards in the
 * list under the same heading (whatever its capitals; a single product's card is headed
 * by its name) — one item sold in several packs, each with its own single:
 *
 *   "Carrots - 24x2lb" + "Carrots - 2lb bag"  and  "Carrots - 10x5" + "Carrots - 5lb bag"
 *     → both "Carrots", and a collapsed card shows only its heading
 *
 * Those groups go by their head's whole name instead, every row "": the card as it was
 * before the heading was worked out. A card already headed by its head's whole name
 * has nothing to fall back to and is left alone. Checked again after each fallback,
 * since a whole name can itself be another group's shared part.
 */
export function sizeCardLabelsInList<P extends { name: string; unit?: string | null }>(
  groups: SizeGroup<P>[],
): SizeCardLabels[] {
  const labels = groups.map(({ head, members }) =>
    members.length > 1 ? sizeCardLabels(members, head.name) : { title: head.name, rows: [""] },
  );
  for (;;) {
    const cardsHeaded = new Map<string, number>();
    for (const { title } of labels) cardsHeaded.set(headingKey(title), (cardsHeaded.get(headingKey(title)) ?? 0) + 1);
    let changed = false;
    groups.forEach(({ head, members }, i) => {
      if (labels[i].title === head.name || cardsHeaded.get(headingKey(labels[i].title)) === 1) return;
      labels[i] = { title: head.name, rows: members.map(() => "") };
      changed = true;
    });
    if (!changed) return labels;
  }
}
